// Labels for each checklist section / field.
//
// The NEW_* / MARK_* / COMP_* (airway…) definitions below mirror the revised
// "AMBULANCE CHECKLIST" Excel form (3-state marks: ✓ / ✗ / N/A). The older
// constants further down are kept ONLY so previously submitted checklists
// still render; the form no longer uses them.
// 3-state item mark: "" (unmarked) | "Y" (✓ available) | "N" (✗ not available)
// | "NA" (not applicable).
export const MARK_STATES = [
    ["Y", "✓"],
    ["N", "✗"],
    ["NA", "N/A"],
];
export const EXTRA_HEADER = {
    remarks: "Remarks",
    expiry: "Nearest Expiry Date",
};
/** Is this a monthly (expiry-collecting) checklist? `date` is YYYY-MM-DD. */
export function isExpiryDay(dateStr) {
    return (dateStr ?? "").split("-")[2] === "01";
}
/**
 * What a section's extra column actually is on a given day: an "expiry"
 * section only collects dates on the monthly form, otherwise it's Remarks.
 */
export function extraKindFor(secExtra, expiryDay) {
    return secExtra === "expiry" && expiryDay ? "expiry" : "remarks";
}
// Vehicle & Mechanical Readiness — mark + remarks per item.
export const VEHICLE_MECHANICAL = [
    ["exteriorBodyClean", "Exterior body clean"],
    ["cabinDisinfected", "Cabin disinfected"],
    ["fuelLevel", "Fuel level > 3/4"],
    ["oilLevel", "Oil level checked"],
    ["radiatorWater", "Radiator water checked"],
    ["batteryTerminals", "Battery terminals secure"],
    ["tiresCondition", "Tires condition and pressure"],
    ["brakes", "Brakes functional"],
    ["headlightsIndicators", "Headlights / indicators"],
    ["siren", "Siren operational"],
    ["driverAC", "Driver A/C functional"],
    ["windshieldWipers", "Windshield wipers"],
    ["mirrors", "Mirrors intact"],
    ["fireExtinguisherValid", "Fire extinguisher valid"],
    ["spareTire", "Spare tire available"],
    ["ambulanceTools", "Ambulance tools available"],
    ["mileageRecorded", "Vehicle mileage recorded"],
    ["medicalWasteDisposed", "Medical waste disposed properly"],
    ["cabinetLights", "Cabinet lights functional"],
    ["dailyClean", "Daily Clean inside \\ out side"],
];
// Medical & Equipment Readiness — qty + mark + remarks per item.
export const MEDICAL_EQUIPMENT = [
    ["radio", "Radio", "1"],
    ["laptopCad", "Tablet - CAD system functional", "1"],
    ["mainOxygen", "Main oxygen >700 PSI", "1"],
    ["portableSuction", "Portable Suction", "1"],
    ["portableOxygen", "Portable oxygen >1000 PSI", "1"],
    ["flowmeter", "Flowmeter", "1"],
    ["oxygenRegulator", "Oxygen regulator operational", "1"],
    ["refrigerator", "Refrigerator clean & temperature logged", "1"],
    ["blanket", "Blanket", "1"],
    ["bedSheet", "Bed sheet", "1"],
    ["headImmobilization", "Head Immobilization", "1"],
    ["scoopSpineBoard", "Scoop Spine board with straps", "1"],
    ["stretcherStraps", "Stretcher with straps", "1"],
    ["extraStrykerBattery", "Extra Stryker battery", "1"],
    ["defibrillator", "Defibrillator operational with all accessories", "1"],
    ["ecgElectrodesAdult", "ECG electrodes Adult", "1"],
    ["ecgElectrodesPedia", "ECG electrodes Pedia", "1"],
    ["defibPadsAdult", "Defibrillation/Pacing Pads Adult", "1"],
    ["defibPadsPedia", "Defibrillation/Pacing Pads Pedia", "1"],
    ["emergencyBag", "Emergency bag", "1"],
];
// Compartment A – Airway — qty + mark + nearest expiry date per item.
export const COMP_AIRWAY = [
    ["opa00", "Oropharyngeal Airway 00", "1"],
    ["opa0", "Oropharyngeal Airway 0", "1"],
    ["opa1", "Oropharyngeal Airway 1", "1"],
    ["opa2", "Oropharyngeal Airway 2", "1"],
    ["opa3", "Oropharyngeal Airway 3", "1"],
    ["opa4", "Oropharyngeal Airway 4", "1"],
    ["npa2", "Nasopharyngeal Airway 2", "1"],
    ["npa3", "Nasopharyngeal Airway 3", "1"],
    ["npa4", "Nasopharyngeal Airway 4", "1"],
    ["npa5", "Nasopharyngeal Airway 5", "1"],
    ["npa6", "Nasopharyngeal Airway 6", "1"],
    ["npa7", "Nasopharyngeal Airway 7", "1"],
    ["npa8", "Nasopharyngeal Airway 8", "1"],
    ["lma1", "Laryngeal Mask (LMA) Size 1", "1"],
    ["lma2", "Laryngeal Mask (LMA) Size 2", "1"],
    ["lma3", "Laryngeal Mask (LMA) Size 3", "1"],
    ["lma4", "Laryngeal Mask (LMA) Size 4", "1"],
    ["lma5", "Laryngeal Mask (LMA) Size 5", "1"],
    ["etHolder", "ET holder (Adult, Pediatric)", "1"],
    ["suctionTube6", "Suction Tube SIZE 6", "2"],
    ["suctionTube8", "Suction Tube SIZE 8", "2"],
    ["suctionTube10", "Suction Tube SIZE 10", "2"],
    ["suctionTube12", "Suction Tube SIZE 12", "2"],
    ["suctionTube14", "Suction Tube SIZE 14", "2"],
    ["suctionTube16", "Suction Tube SIZE 16", "2"],
    ["yankauerSuction", "Yankauer Suction", "2"],
    ["kyGel", "KY Gel", "1"],
];
// Compartment B – Breathing.
export const COMP_BREATHING = [
    ["bvmAdult", "Bag Valve Mask Adult", "1"],
    ["bvmPediatric", "Bag Valve Mask Pediatric", "1"],
    ["bvmNeonate", "Bag Valve Mask Neonate", "1"],
    ["nebulizerKitAdult", "Nebulizer Kit Adult", "2"],
    ["nebulizerKitPedis", "Nebulizer Kit Pedis", "2"],
    ["oxygenMaskAdult", "Oxygen Mask Adult", "2"],
    ["oxygenMaskPedis", "Oxygen Mask Pedis", "2"],
    ["nrbMaskAdult", "Non rebreathing Mask Adult", "2"],
    ["nrbMaskPedis", "Non rebreathing Mask Pedis", "2"],
    ["nasalCannulaAdult", "Nasal Cannula Adult", "2"],
    ["nasalCannulaPedis", "Nasal Cannula Pedis", "2"],
];
// Compartment C – Circulation. NOTE: "Cannula G16" appears twice on the
// official form (rows 5 and 8); both rows are kept, with distinct keys.
export const COMP_CIRCULATION = [
    ["syringe1cc", "Syringe 1 CC", "2"],
    ["syringe3cc", "Syringe 3 CC", "2"],
    ["syringe5cc", "Syringe 5 CC", "2"],
    ["syringe10cc", "Syringe 10 CC", "2"],
    ["cannulaG16", "Cannula G16", "2"],
    ["cannulaG18", "Cannula G18", "2"],
    ["cannulaG14", "Cannula G14", "2"],
    ["cannulaG16b", "Cannula G16", "2"],
    ["cannulaG20", "Cannula G20", "2"],
    ["cannulaG22", "Cannula G22", "2"],
    ["cannulaG24", "Cannula G24", "2"],
    ["cannulaG26", "Cannula G26", "2"],
    ["needle18Gx38mm", "Needles injection hypodermic 18GX38MM", "2"],
    ["needle20Gx25mm", "Needles injection hypodermic 20G X25MM", "1"],
    ["needle23Gx25mm", "Needles injection hypodermic 23G X25MM", "1"],
    ["needle25Gx25mm", "Needles injection hypodermic 25GX25MM", "2"],
    ["needle26Gx125mm", "Needles injection hypodermic 26GX12.5MM", "2"],
    ["needle30Gx125mm", "Needles injection hypodermic 30GX12.5MM", "2"],
    ["glucometerStrips", "Glucometer strips", "1Box"],
    ["ivSetLine", "IV set line", "4"],
    ["ivExtensionLine", "IV Extension line", "2"],
    ["threeWayStopcock", "3-way stopcock", "2"],
    ["ivStopper", "IV Stopper", "2"],
    ["tourniquet", "Tourniquet", "1Roll"],
    ["alcoholSwap", "Alcohol Swap", "1box"],
    ["plaster", "Plaster", "2"],
    ["pressureIvBag", "Pressure IV bag", "1"],
];
// Compartment E – OB & Emergency Kit.
export const COMP_OB = [
    ["obKit", "OB kit complete", "1"],
    ["icePack", "Ice pack", "10"],
    ["salineIrrigationBottles", "Saline irrigation bottles", "1"],
    ["pelvicBinder", "Pelvic binder", "1"],
    ["pediatricLengthTape", "Pediatric length-based tape", "1"],
];
// Compartment D – Disability.
export const COMP_IMMOBILIZATION = [
    ["cCollarAdult", "C-collar Adult", "1"],
    ["cCollarPedia", "C-collar Pedia", "1"],
    ["triangularBandage", "Triangular bandage", "2"],
    ["crepeBandage", "Crepe bandage", "3"],
    ["eyeShield", "Eye shield", "3"],
    ["burnDressing", "Burn dressing", "5"],
    ["burnSheet", "Burn sheet", "2"],
    ["rollerBandage", "Roller bandage", "3"],
    ["samSplint", "Sam Splint", "2"],
    ["obstetricKit", "Obstetric Kit", "1"],
    ["salineIrrigationBottles", "Saline irrigation bottles", "2"],
    ["gauze10x10", "Gauze 10X10", "2Box"],
    ["gauze5x5", "Gauze 5X5", "2"],
    ["gauze2x2", "Gauze 2X2", "2"],
    ["sterileGauze", "Sterile gauze", "3"],
    ["bandagesCrepeElastic", "Bandages Crepe Elastic Roll 7.5Cmx4.5M", "3"],
];
// Compartment F – Infection Control & PPE.
export const COMP_INFECTION = [
    ["surgicalMask", "Surgical Mask", "1Box"],
    ["n95Mask", "N95 Mask", "1Box"],
    ["glovesSmall", "Disposable Gloves Small", "1Box"],
    ["glovesMedium", "Disposable Gloves Medim", "1Box"],
    ["glovesLarge", "Disposable Gloves Large", "1Box"],
    ["yellowGown", "Yellow gown", "1Bag"],
    ["faceShield", "Face Shield", "2"],
    ["biohazardBag", "Biohazard bag", "3"],
    ["trashBag", "Trash bag", "1"],
    ["bloodSpillKit", "Blood Spill kit", "1"],
    ["handSanitizer", "Hand Sanitizer", "1"],
    ["disposablePaperBedSheet", "Disposable Paper Bed Sheet", "1Roll"],
    ["wipes", "Wipes", "1"],
    ["sharpContainer", "Sharp container", "1"],
];
// ACLS Bag — qty + mark + nearest expiry date per item (plus a seal number).
// NOTE: "Cannula G16" appears twice on the official form; both rows are kept.
export const ACLS_ITEMS = [
    ["medicationBagSealed", "Medication bag available and sealed", "1"],
    ["opa0", "Oropharyngeal Airway 0", "1"],
    ["opa1", "Oropharyngeal Airway 1", "1"],
    ["opa2", "Oropharyngeal Airway 2", "1"],
    ["opa3", "Oropharyngeal Airway 3", "1"],
    ["opa4", "Oropharyngeal Airway 4", "1"],
    ["npa2", "Nasopharyngeal Airway 2", "1"],
    ["npa3", "Nasopharyngeal Airway 3", "1"],
    ["npa4", "Nasopharyngeal Airway 4", "1"],
    ["npa5", "Nasopharyngeal Airway 5", "1"],
    ["npa6", "Nasopharyngeal Airway 6", "1"],
    ["npa7", "Nasopharyngeal Airway 7", "1"],
    ["npa8", "Nasopharyngeal Airway 8", "1"],
    ["syringe1cc", "Syringe 1 CC", "2"],
    ["syringe3cc", "Syringe 3 CC", "2"],
    ["syringe5cc", "Syringe 5 CC", "2"],
    ["syringe10cc", "Syringe 10 CC", "2"],
    ["cannulaG16", "Cannula G16", "2"],
    ["cannulaG18", "Cannula G18", "2"],
    ["cannulaG14", "Cannula G14", "2"],
    ["cannulaG16b", "Cannula G16", "2"],
    ["cannulaG20", "Cannula G20", "2"],
    ["cannulaG22", "Cannula G22", "2"],
    ["cannulaG24", "Cannula G24", "2"],
    ["cannulaG26", "Cannula G26", "2"],
    ["plaster", "PLASTER", "1"],
    ["tourniquet", "Tourniquet", "1Roll"],
    ["alcoholSwap", "Alcohol Swap", "1 Box"],
    ["tegadermPlasterTourniquet", "Tegaderm & Plaster & Tourniquet", "1"],
    ["gauze4x4", "GAUZE4X4", "2 Box"],
    ["paraffinGauze10x10", "PARAFFIN GAUZE 10X10", "4"],
    ["paraffinGauze5x5", "PARAFFIN GAUZE 5X5", "4"],
    ["crepeBandage10cm", "CREPE BANDGE10CMX4.5M", "2"],
    ["conformingBandage", "CONFIRMINH BANDAGE", "2"],
    ["cannulaSet", "CANNULA SET", "4"],
    ["nebulizerKitAdult", "Nebulizer Kit Adult", "2"],
    ["nebulizerKitPedis", "Nebulizer Kit Pedis", "2"],
    ["oxygenMaskAdult", "Oxygen Mask Adult", "2"],
    ["oxygenMaskPedis", "Oxygen Mask Pedis", "2"],
    ["nrbMaskAdult", "Non rebreathing Mask Adult", "2"],
    ["nrbMaskPedis", "Non rebreathing Mask Pedis", "2"],
    ["nasalCannulaAdult", "Nasal Cannula Adult", "2"],
    ["nasalCannulaPedis", "Nasal Cannula Pedis", "2"],
    ["eyePads", "Eye Pads", "3"],
    ["razor", "RAZOR", "1"],
    ["lma1", "Laryngeal Mask (LMA) Size 1", "1"],
    ["lma2", "Laryngeal Mask (LMA) Size 2", "1"],
    ["lma3", "Laryngeal Mask (LMA) Size 3", "1"],
    ["lma4", "Laryngeal Mask (LMA) Size 4", "1"],
    ["lma5", "Laryngeal Mask (LMA) Size 5", "1"],
    ["etHolderAdult", "ET holder (Adult)", "1"],
    ["etHolderPedia", "ET holder (Pedia)", "1"],
    ["icePack", "Ice Pack", "2"],
    ["flushSalineSyringe", "FLUSH SALINE SYRINGE", "3"],
    ["cCollarAdult", "C-collar Adult", "1"],
    ["triangularBandage", "Triangular Bandage", "1"],
    ["glucometer", "Glucometer", "1"],
    ["lancets", "Lancets", "1Box"],
    ["bloodPressureDevice", "Blood Pressure device", "1"],
    ["tympanicThermometer", "Tympanic Thermometer", "1"],
    ["thermometerProbeCovers", "Thermometer Probe Covers", "1Box"],
];
// Items inside an "expiry" section that do NOT carry a nearest expiry date
// (they show no date field on the form, and "—" in the expiry column on print).
// Durable equipment only — everything sterile / consumable keeps its date.
export const NO_EXPIRY_ITEMS = new Set([
    "etHolder",
    "etHolderAdult",
    "etHolderPedia",
    "cCollarAdult",
    "cCollarPedia",
    "samSplint",
    "pelvicBinder",
    "pediatricLengthTape",
    "biohazardBag",
    "trashBag",
    "sharpContainer",
    "glucometer",
    "bloodPressureDevice",
    "tympanicThermometer",
]);
// One entry per new section: the data field holding the marks, the field
// holding the per-item extra (remarks or expiry date), and its kind. Order
// mirrors the official Excel form — left column top-to-bottom, then right.
export const NEW_CHECKLIST_SECTIONS = [
    {
        key: "vehicleMechanical",
        extraKey: "vehicleMechanicalRemarks",
        extra: "remarks",
        label: "Vehicle & Mechanical Readiness",
        items: VEHICLE_MECHANICAL,
    },
    {
        key: "medicalEquipment",
        extraKey: "medicalEquipmentRemarks",
        extra: "remarks",
        label: "Medical & Equipment Readiness",
        items: MEDICAL_EQUIPMENT,
    },
    {
        key: "compAirway",
        extraKey: "compAirwayExpiry",
        extra: "expiry",
        label: "Compartment A – Airway",
        items: COMP_AIRWAY,
    },
    {
        key: "compBreathing",
        extraKey: "compBreathingExpiry",
        extra: "expiry",
        label: "Compartment B – Breathing",
        items: COMP_BREATHING,
    },
    {
        key: "compCirculation",
        extraKey: "compCirculationExpiry",
        extra: "expiry",
        label: "Compartment C – Circulation",
        items: COMP_CIRCULATION,
    },
    {
        key: "compOb",
        extraKey: "compObExpiry",
        extra: "expiry",
        label: "Compartment E – OB & Emergency Kit",
        items: COMP_OB,
    },
    {
        key: "compImmobilization",
        extraKey: "compImmobilizationExpiry",
        extra: "expiry",
        label: "Compartment D – Disability",
        items: COMP_IMMOBILIZATION,
    },
    {
        key: "compInfection",
        extraKey: "compInfectionExpiry",
        extra: "expiry",
        label: "Compartment F – Infection Control & PPE",
        items: COMP_INFECTION,
    },
    {
        key: "aclsItems",
        extraKey: "aclsExpiry",
        extra: "expiry",
        label: "ACLS Bag",
        items: ACLS_ITEMS,
    },
];
// ——— Legacy definitions (old submitted checklists only) ———
export const VEHICLE_READINESS = [
    ["startingUp", "Starting Up"],
    ["externalBodyClean", "External Body Clean"],
    ["driverACWorking", "Driver A/C Working"],
    ["frontalLights", "Frontal Lights"],
    ["tiersAirConditionSpare", "Tiers' Air & Condition & Spare"],
    ["windshieldWiper", "Windshield Wiper"],
    ["vehicleBrake", "Vehicle Brake"],
    ["warningLight", "Warning Light"],
    ["siren", "Siren"],
    ["radiatorWater", "Radiator Water"],
    ["battery", "Battery"],
    ["mirrors", "Mirrors"],
    ["fuelLevel", "Fuel level is > 3/4"],
];
export const COMMUNICATION_TECH = [
    ["cameraWorking", "Camera is working"],
    ["headSetMicrophone", "Head Set & Microphone"],
    ["radio", "Radio"],
    ["lapTop", "Lap top"],
    ["internetNetwork", "Internet Network"],
    ["vidaAccess", "VIDA Access"],
];
export const PATIENT_CABINET = [
    ["decontaminated", "Decontaminated"],
    ["cleanTidy", "Clean & Tidy"],
    ["sharpContainer", "Sharp Container > 1/3 empty"],
    ["garbageEmpty", "Garbage is empty"],
    ["cabinetAttachedPlug", "Cabinet attached to Plug"],
    ["acWorking", "A/C Working"],
    ["compartmentLightsWorking", "Compartment Lights Working"],
];
export const FIX_ASSETS_COL1 = [
    ["mainOxygen", "Main Oxygen > 700 Psi."],
    ["portableOxygen", "Portable Oxygen > 1000 Psi."],
    ["oxygenRegulator", "Oxygen Regulator"],
    ["scopeBoard", "Scope Board"],
    ["triageTags50", "Triage Tags 50"],
    ["installedSuctionBags", "Installed Suction with Bags"],
    ["portableSuction", "Portable Suction"],
    ["clipBoardReport", "Clip Board & Report"],
    ["tissuePaper", "Tissue Paper"],
    ["handSanitizerBottle", "Hand Sanitizer Bottle"],
    ["sanitizerWipesBox", "Sanitizer Wipes Box"],
    ["minutenSprayBottle", "Minuten Spray Bottle"],
    ["disposableGloves", "Disposable Gloves S,M,L,XL"],
];
export const FIX_ASSETS_COL2 = [
    ["cleanRefrigerator", "Clean Refrigerator"],
    ["refrigTemperatureList", "Refrig. Temperature List"],
    ["fireExtinguisher", "Fire Extinguisher"],
    ["twoBlanket", "2 Blanket"],
    ["twoBedSheet", "2 Bed sheet"],
    ["pillow", "Pillow"],
    ["strykerWith2Straps", "Stryker with 2 Straps"],
    ["batteryAttachedStryker", "Battery attached to Stryker"],
    ["extraStrykerBattery", "Extra Stryker Battery on Charger"],
    ["stairChair", "Stair Chair with 2 straps"],
    ["foldingStretcher", "Folding Stretcher with 5 Straps"],
];
export const DEFIBRILLATOR = [
    ["available", "Defibrillator Available"],
    ["twoBattery", "2 Battery"],
    ["bpCuff", "BP Cuff (Adult & Pedia)"],
    ["spo2Sensor", "SpO2 Sensor (Adult & Pedia)"],
    ["extraECGPaper", "Extra Roll of ECG Paper"],
    ["pads", "Pads (Adult & Pedia)"],
    ["ecgElectrodes", "ECG Electrodes (Adult & Pedia)"],
    ["twelveLeadsCable", "12 Leads ECG Cable"],
    ["pediatricPaddles", "Pediatric Paddles"],
    ["razor", "Razor"],
];
export const ALS_RIGHT_POUCH = [
    ["manualBPCuff", "Manual BP Cuff + Stethoscope (Adult & Pedia)", "1"],
    ["sterileWaterBottle", "Sterile Water Bottle 1000ml (irrigation)", "1"],
];
export const ALS_LEFT_POUCH = [
    ["sharpContainer", "Sharp container", "1"],
    ["samSplint", "Sam Splint", "1"],
];
export const ALS_MAIN_COMPARTMENT = [
    ["ivCannula", "IV Cannula (18,20,22,24)", "2"],
    ["tegadermPlasterTourniquet", "Tegaderm & Plaster & Tourniquet", "2"],
    ["syringes", "Syringes (1,3,5,10,20,50 ML)", "3"],
    ["ivSetInfusion", "IV Set Infusion & Pressure IV fluid Bag", "2"],
    ["threeWayStopcock", "Three Way stopcock", "2"],
    ["alcoholSwab", "Alcohol Swab", "1 PKG"],
    ["bvm", "BVM (Adult & Pedia & Neonate)", "1"],
    ["ettCuffedUncuffed", "ETT cuffed & Uncuffed (3 → 9)", "1"],
    ["lma", "LMA (1, 1.5, 2, 2.5, 3, 4, 5)", "1"],
    ["kingTube", "King Tube (1,2,3,4,5)", "1"],
    ["softSuctionTube", "Soft Suction Tube (6,8,10,12,14,16)", "1"],
    ["tenMlSyringe", "10 ml Syringe", "2"],
    ["etSecureKYJelly", "ET Secure & KY Jelly", "2"],
    ["co2Detector", "CO2 Detector (Adult & Pedia)", "2"],
    ["styletBougi", "Stylet & Bougi (Adult & Pedia)", "1"],
    ["magillForceps", "Magill Forceps (Adult & Pedia)", "1"],
    ["fourteenGNeedle", "14 G Needle (Decompression)", "1"],
];
export const ALS_INTUBATION = [
    ["laryngoscopeHandle", "Laryngoscope Handle Thick & Thin", "1"],
    ["laryngoscopeBlade", "Laryngoscope Blade Mac & Mill (0,1,2,3,4)", "1"],
    ["spareBattery", "Spare Battery", "2"],
];
export const UNDER_BENCH = [
    ["backBoardWith5Straps", "Back Board with 5 Straps"],
    ["spiderHarness", "Spider Harness (Optional)"],
    ["headImmobilizer", "Head Immobilizer (Adult & Pedia)"],
    ["cCollar", "C-Collar (Adult & Pedia)"],
    ["splintSamSplint", "Splint & SAM splint"],
    ["extraStraps", "Extra Straps"],
    ["fullPortableOxygen", "Full Portable Oxygen Size D"],
];
export const COMP_A = [
    ["nasalCannula", "Nasal Cannula (Adult & Pedia)", "2"],
    ["nebulizerKit", "Nebulizer Kit with Mask (Adult & Pedia)", "2"],
    ["nonrebreatherMask", "Nonrebreather Mask (Adult & Pedia)", "2"],
    ["simpleMask", "Simple Mask (Adult & Pedia)", "2"],
];
export const COMP_B = [
    ["suctionTubing", "Suction Tubing", "2"],
    ["rigidSuctionTube", "Rigid Suction Tube", "2"],
    ["softSuctionTube", "Soft Suction Tube (6,8,10,12,14,16)", "2"],
    ["stethoscope", "Stethoscope (Adult & Pedia)", "—"],
];
export const COMP_C = [
    ["surgicalMask", "Surgical Mask", "1 PKG"],
    ["yellowGown", "Yellow Gown & Surgical Gown", "1 PKG"],
    ["headCoverShoesCover", "Head Cover & Shoes Cover", "1 PKG"],
    ["n95Mask", "N95 Mask S,M,L", "2"],
    ["goggles", "Goggles", "2"],
    ["faceShield", "Face Shield", "2"],
    ["biohazardBag", "Biohazard Bag", "2"],
];
export const COMP_D = [
    ["ivCannula", "IV Cannula (18,20,22,24)", "3"],
    ["syringe", "Syringe (1,3,5,10,20,50)", "5"],
    ["ivInfusionSet", "IV Infusion Set & Three Way Stopcock", "5"],
    ["alcoholSwap", "Alcohol Swap", "1 PKG"],
    ["tourniquet", "Tourniquet", "2"],
    ["tegadermBlaster", "Tegaderm & Blaster", "10"],
    ["pressureIVFluidBag", "Pressure IV fluid Bag", "1"],
    ["glucometerKit", "Glucometer Kit & Tympanic Thermometer", "—"],
    ["urinal", "Urinal", "1"],
];
export const COMP_E = [
    ["crepeBandage", "Crepe Bandage (15*4.5 / 10*4.5 cm)", "2"],
    ["slinkyBandage", "Slinky Bandage (12*4 / 10*4 / 8*4 cm)", "2"],
    ["gauze", "Gauze (2*2 & 4*4 inch)", "3"],
    ["sterileGauze", "Sterile Gauze", "10"],
    ["sterileGloves", "Sterile Gloves 6,7,8", "2"],
    ["opsite", "Opsite (15.5*8.5 / 9.5*8.5 cm)", "3"],
    ["primapore", "Primapore (25*10 / 15*8 / 8.3*6 cm)", "5"],
    ["multiTraumaDressing", "Multi Trauma Dressing S, M, L", "2"],
    ["triangularBandage", "Triangular Bandage", "3"],
    ["icePackEyePadsVomitingBag", "Ice Pack & Eye Pads & Vomiting Bag", "5"],
    ["underPads", "Under Pads (Chux)", "5"],
    ["bottleSterileH2O", "Bottle Sterile H2O for Irrigation", "1"],
    ["burnPads", "Burn Pads", "1"],
    ["scissor", "Scissor", "—"],
    ["obKit", "OB Kit (Baby Blanket, Umbilical Cord Clamps, Scissors/Scalpel, Bulb Syringe, Placenta Basin, Plastic Pad)", "1"],
];
export const FRONT_POUCH = [
    ["opaNpaKYJelly", "OPA (00,1,2,3,4) & NPA (6,7,8) & KY Jelly", "1"],
    ["glucometerStripsLancets", "Glucometer & Strips & Lancets", "1"],
    ["sterileGloves", "Sterile Gloves (6,7,8)", "2"],
];
export const REAR_POUCH = [
    ["crepeBandage", "Crepe Bandage (15*4.5 / 10*4.5 cm)", "2"],
    ["gauze", "Gauze (2*2, 4*4)", "2"],
    ["bandageScissor", "Bandage Scissor", "1"],
    ["triangleBandage", "Triangle Bandage", "2"],
];
export const IMAGE_SLOTS = [
    { key: "front", label: "Front" },
    { key: "back", label: "Back" },
    { key: "right", label: "Right Side" },
    { key: "left", label: "Left Side" },
    { key: "inside1", label: "Inside 1" },
    { key: "inside2", label: "Inside 2" },
];
//# sourceMappingURL=checklistFields.js.map